import { useAuth } from '@/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useCallback, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/common/utils/apiError';
import { PreferencesService, UserPreferences } from '../services/preferencesService';


const PREFERENCES_QUERY_KEY = 'user-preferences';
const DEBOUNCE_DELAY = 500; // 500ms
const STALE_TIME = 5 * 60 * 1000; // 5 minutes

/** Referencia estable: un `{}` nuevo por render re-dispara efectos aguas abajo. */
const EMPTY_PREFERENCES: UserPreferences = {};

/** Guardar preferencias es secundario: se avisa una vez, sin apilar toasts. */
const PREFERENCES_ERROR_TOAST_ID = 'preferences-save-error';

/**
 * Hook for managing user preferences with DB persistence
 */
export function usePreferences() {
  const { user, isAuth } = useAuth();
  const queryClient = useQueryClient();

  // Fetch preferences from server
  const isEnabled = isAuth && !!user;

  const {
    data: preferencesData,
    isLoading: isQueryLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: [PREFERENCES_QUERY_KEY, user?.id],
    queryFn: () => PreferencesService.getPreferences(),
    enabled: isEnabled,
    staleTime: STALE_TIME,
    retry: 2,
    meta: { operation: 'preferencias:cargar' },
  });

  /**
   * Mientras la query está deshabilitada (auth todavía sin resolver) React Query
   * informa `isLoading: false` aunque no haya datos. Quien consume esto lo toma
   * como "las preferencias ya llegaron y están vacías" y se queda con los
   * defaults para siempre: la vista kanban y los filtros guardados se pierden.
   */
  const isLoading = !isEnabled || isQueryLoading;

  /** true cuando ya se sabe qué preferencias tiene el usuario (o que fallaron) */
  const isResolved = isEnabled && !isQueryLoading;

  const preferences = useMemo(
    () => preferencesData?.data?.preferences || EMPTY_PREFERENCES,
    [preferencesData],
  );

  // Mutation for updating preferences
  const updateMutation = useMutation({
    mutationFn: (newPreferences: UserPreferences) =>
      PreferencesService.updatePreferences(newPreferences),
    onMutate: async (newPreferences) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: [PREFERENCES_QUERY_KEY, user?.id] });

      // Snapshot previous value
      const previousPreferences = queryClient.getQueryData([PREFERENCES_QUERY_KEY, user?.id]);

      // Optimistically update
      queryClient.setQueryData([PREFERENCES_QUERY_KEY, user?.id], {
        success: true,
        data: { preferences: newPreferences },
        message: 'Updated',
      });

      return { previousPreferences };
    },
    onError: (err, newPreferences, context) => {
      // Rollback on error
      if (context?.previousPreferences) {
        queryClient.setQueryData(
          [PREFERENCES_QUERY_KEY, user?.id],
          context.previousPreferences
        );
      }

      toast.error(
        getApiErrorMessage(err, 'No se pudieron guardar tus preferencias.'),
        { id: PREFERENCES_ERROR_TOAST_ID },
      );
    },
    meta: { operation: 'preferencias:guardar' },
  });

  // Mutation for partial updates
  const updatePartialMutation = useMutation({
    mutationFn: ({ path, value }: { path: string; value: any }) =>
      PreferencesService.updatePartialPreferences(path, value),
    onSuccess: (data) => {
      // Update cache with new full preferences from server
      queryClient.setQueryData([PREFERENCES_QUERY_KEY, user?.id], data);
    },
    // Antes esto sólo hacía console.error: el usuario veía el cambio aplicado en
    // pantalla, no se guardaba, y al recargar le volvía el valor viejo sin
    // ninguna explicación.
    onError: (err) => {
      toast.error(
        getApiErrorMessage(err, 'No se pudo guardar tu preferencia.'),
        { id: PREFERENCES_ERROR_TOAST_ID },
      );
    },
    meta: { operation: 'preferencias:guardar-parcial' },
  });

  // Mutation for reset
  const resetMutation = useMutation({
    mutationFn: () => PreferencesService.resetPreferences(),
    onSuccess: (data) => {
      queryClient.setQueryData([PREFERENCES_QUERY_KEY, user?.id], data);
      toast.success('Preferencias restablecidas');
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'No se pudieron restablecer las preferencias.'));
    },
    meta: { operation: 'preferencias:restablecer' },
  });

  // Debounced update function
  const debouncedUpdateRef = useRef(
    debounce((newPreferences: UserPreferences) => {
      updateMutation.mutate(newPreferences);
    }, DEBOUNCE_DELAY)
  );

  /**
   * Update preferences with debouncing
   */
  const updatePreferences = useCallback(
    (newPreferences: UserPreferences) => {
      debouncedUpdateRef.current(newPreferences);
    },
    []
  );

  /**
   * Update partial preference using dot notation
   */
  const updatePartialPreference = useCallback(
    (path: string, value: any) => {
      updatePartialMutation.mutate({ path, value });
    },
    [updatePartialMutation]
  );

  /**
   * Reset preferences to defaults
   */
  const resetPreferences = useCallback(() => {
    resetMutation.mutate();
  }, [resetMutation]);

  /**
   * Get preference value by path with fallback
   */
  const getPreference = useCallback(
    <T,>(path: string, defaultValue: T): T => {
      // Try to get from server preferences
      const keys = path.split('.');
      let value: any = preferences;

      for (const key of keys) {
        if (value && typeof value === 'object' && key in value) {
          value = value[key];
        } else {
          return defaultValue;
        }
      }

      return value as T;
    },
    [preferences]
  );

  return {
    preferences,
    isLoading,
    /** Ya se resolvió la carga (con datos o con error): recién acá se puede decidir */
    isResolved,
    isError,
    error,
    /** Reintentar la carga tras un fallo */
    refetchPreferences: refetch,
    updatePreferences,
    updatePartialPreference,
    resetPreferences,
    getPreference,
    isSaving: updateMutation.isPending || updatePartialMutation.isPending,
  };
}
